// Epic 8 follow-on — apply-page DRAFTS.
//
// GET  /api/v1/creators/application-draft — fetch my saved draft (or null),
//   used to rehydrate the apply form on load.
// POST /api/v1/creators/application-draft — upsert my single draft. Every
//   field optional; NO mandatory photo/signature at draft stage.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type { LinkedAccountInput } from "./useSubmitCreatorApplication";

export type CreatorApplicationDraft = {
  display_name: string | null;
  hello_text: string | null;
  image_blob_path: string | null;
  image_signed_url: string | null;
  signature_blob_path: string | null;
  signature_signed_url: string | null;
  linked_accounts: LinkedAccountInput[] | null;
  region: string | null;
  language: string | null;
  /** Story 8.9 — the identity block, saved and rehydrated like every other
   *  draft field so it survives a reload and the Twitch OAuth round-trip.
   *  Individually nullable: a half-filled form is the normal state of a
   *  draft. No `tin` — that column is dormant. */
  legal_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  city: string | null;
  country_code: string | null;
  business_status: string | null;
  business_registration_number: string | null;
  updated_at: string;
};

export type CreatorApplicationDraftEnvelope = {
  data: CreatorApplicationDraft | null;
};

export type SaveDraftRequest = {
  display_name?: string | null;
  hello_text?: string | null;
  image_blob_path?: string | null;
  signature_blob_path?: string | null;
  linked_accounts?: LinkedAccountInput[];
  region?: string | null;
  language?: string | null;
  tos_version_accepted_id?: string | null;
  // Story 8.9. Every one is optional and nullable: unlike a submission, a
  // draft may carry any subset. The server drops a value that would break a
  // migration-0094 CHECK (a half-typed country code, a registration number
  // with no business declaration behind it) rather than failing the save.
  legal_name?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country_code?: string | null;
  business_status?: string | null;
  business_registration_number?: string | null;
};

async function fetchDraft(): Promise<CreatorApplicationDraftEnvelope> {
  const response = await fetch("/api/v1/creators/application-draft", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`application-draft fetch failed: ${response.status}`);
  }
  return (await response.json()) as CreatorApplicationDraftEnvelope;
}

export function useCreatorApplicationDraft() {
  return useQuery({
    queryKey: ["creator-application-draft"],
    queryFn: fetchDraft,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}

async function postDraft(
  body: SaveDraftRequest,
): Promise<CreatorApplicationDraft> {
  const response = await fetch("/api/v1/creators/application-draft", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: { code?: string; message?: string } = {
      code: "DRAFT_SAVE_FAILED",
      message: `Draft save failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const envelope = (await response.json()) as {
    data: CreatorApplicationDraft;
  };
  return envelope.data;
}

export function useSaveCreatorApplicationDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postDraft,
    onSuccess: (data) => {
      queryClient.setQueryData<CreatorApplicationDraftEnvelope>(
        ["creator-application-draft"],
        { data },
      );
    },
  });
}
